import { doc, updateDoc } from "firebase/firestore/lite";
import React, { useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { db, useAuthContext } from "senf-shared";
import { useIsMobile } from "../../../hooks/customDeviceDetect";
import Button from "../../core/button/Button";
import Checkbox from "../../core/checkbox/Checkbox";
import { useModals } from "../../molecules/modalStack/ModalProvider";

const DataPrivacyModal = ({ onSubmit, project }) => {
  const { t } = useTranslation();
  const [checked, setChecked] = useState(false);

  return (
    <div className="flex w-full flex-col items-center justify-center gap-6 p-5 pt-10 lg:pt-5">
      <h2 className="heading-lg">
        {t("auth:dataProtection.declarationOfConsent")}
      </h2>

      <span className="body-md -mt-5 text-center">
        <Trans i18nKey="auth:dataProtection.declarationOfConsentDescription">
          ...
          <a
            href={`/projects/${project?.projectId}/privacyPolicy`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: "underline" }}
          ></a>
          ...
        </Trans>
      </span>
      <Checkbox
        checked={checked}
        label={t("auth:dataProtection.declarationOfConsentCheckboxLabel")}
        aria-label="select"
        tabIndex={0}
        onClick={() => setChecked(!checked)}
      />
      <Button
        text={t("global:next")}
        onClick={() => onSubmit(checked)}
        disabled={!checked}
        variant="primary"
        size="lg"
        className="w-full"
        classNameWrapper="w-full"
      />
    </div>
  );
};

const useDataPrivacyShield = (project) => {
  const { openModal, closeModal } = useModals();
  const isMobile = useIsMobile();
  const { user, setUser, getUserData } = useAuthContext();
  const resolvePromiseRef = useRef(null);

  const handleDataPrivacy = () => {
    const currentUrl = window.location.href;
    if (
      currentUrl.startsWith("https://demo.dashboard.senf.app") ||
      currentUrl.startsWith("http://demo.dashboard.senf.app") ||
      currentUrl.startsWith("https://dashboard.senf.app") ||
      currentUrl.startsWith("http://dashboard.senf.app") ||
      currentUrl.startsWith("http://localhost:3001") ||
      currentUrl.startsWith("https://localhost:3001") ||
      currentUrl.startsWith("http://localhost:4173") ||
      currentUrl.startsWith("https://localhost:4173") ||
      currentUrl.startsWith("http://monitoring.senf.app") ||
      currentUrl.startsWith("https://monitoring.senf.app") ||
      currentUrl.startsWith("http://test.monitoring.senf.app") ||
      currentUrl.startsWith("https://test.monitoring.senf.app") ||
      project?.settings?.declarationOfConsent === false
    ) {
      return true;
    }

    return new Promise((resolve) => {
      resolvePromiseRef.current = resolve;
      openModal(
        <DataPrivacyModal onSubmit={handleSubmit} project={project} />,
        {
          swipe: !!isMobile,
          beforeClose: handleCancel,
          onClose: false, // Handle modal close
          zIndex: 99999999,
          id: "dataPrivacyModal",
        },
      );
    });
  };

  const handleSubmit = (checked) => {
    if (checked && resolvePromiseRef.current) {
      resolvePromiseRef.current(true);
      localStorage.setItem("declarationOfConsent", "true");
    } else if (resolvePromiseRef.current) {
      resolvePromiseRef.current(false);
    }
    closeModal({ id: "dataPrivacyModal" });
  };

  const handleCancel = () => {
    if (resolvePromiseRef.current) {
      resolvePromiseRef.current(false); // Resolve with false to indicate cancellation
    }
    closeModal({ id: "dataPrivacyModal" });
  };

  return { handleDataPrivacy };
};

export default useDataPrivacyShield;
